import { z } from "zod";
import { bookTemplates } from "./catalog.js";
import { SIMULATED_PRINT_NOTICE } from "./simulated-print.js";
export const sharedTestGiftSchemaVersionSchema = z.literal("2026-09-21.2");
export const sharedTestGiftCampaignIdSchema = z
    .string()
    .min(3)
    .max(80)
    .regex(/^[a-z][a-z0-9-]+$/u);
export const sharedTestGiftCampaignStateSchema = z.enum(["ACTIVE", "PAUSED", "REVOKED"]);
export const sharedTestGiftPaidProviderAccessSchema = z.enum(["DISABLED", "BOUNDED"]);
const currentTemplateIds = bookTemplates.map((template) => template.id);
export const sharedTestGiftTemplateIdSchema = z.enum(currentTemplateIds);
export const sharedTestGiftCampaignSchema = z
    .object({
    schemaVersion: sharedTestGiftSchemaVersionSchema,
    id: sharedTestGiftCampaignIdSchema,
    displayName: z.string().trim().min(3).max(160),
    codeHash: z.string().regex(/^[a-f0-9]{64}$/u),
    state: sharedTestGiftCampaignStateSchema,
    allowedTemplateIds: z
        .array(sharedTestGiftTemplateIdSchema)
        .length(bookTemplates.length)
        .refine((templateIds) => new Set(templateIds).size === templateIds.length, "Allowed template identifiers must be unique."),
    maxBooksPerUser: z.number().int().positive().max(100),
    maxBooksTotal: z.number().int().positive().max(10_000),
    maxBooksPerUtcDay: z.number().int().positive().max(1_000),
    paidProviderAccess: sharedTestGiftPaidProviderAccessSchema,
    maxPaidPictureBooksPerUtcDay: z.number().int().nonnegative().max(50),
    maxPaidProviderActionsPerUserPerUtcDay: z.number().int().nonnegative().max(500),
    maxPaidProviderActionsPerUtcDay: z.number().int().nonnegative().max(2_000),
    startsAt: z.string().datetime().optional(),
    expiresAt: z.string().datetime().optional(),
    reusable: z.literal(true),
    test: z.literal(true),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict()
    .superRefine((campaign, context) => {
    if (campaign.startsAt &&
        campaign.expiresAt &&
        Date.parse(campaign.expiresAt) <= Date.parse(campaign.startsAt))
        context.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "Campaign expiry must follow its start time.",
        });
    if (campaign.paidProviderAccess === "DISABLED") {
        for (const field of [
            "maxPaidPictureBooksPerUtcDay",
            "maxPaidProviderActionsPerUserPerUtcDay",
            "maxPaidProviderActionsPerUtcDay",
        ])
            if (campaign[field] !== 0)
                context.addIssue({
                    code: "custom",
                    path: [field],
                    message: "Disabled paid-provider access requires zero paid-provider quotas.",
                });
    }
    if (campaign.paidProviderAccess === "BOUNDED") {
        for (const field of [
            "maxPaidPictureBooksPerUtcDay",
            "maxPaidProviderActionsPerUserPerUtcDay",
            "maxPaidProviderActionsPerUtcDay",
        ])
            if (campaign[field] < 1)
                context.addIssue({
                    code: "custom",
                    path: [field],
                    message: "Bounded paid-provider access requires positive paid-provider quotas.",
                });
        if (campaign.maxPaidProviderActionsPerUserPerUtcDay > campaign.maxPaidProviderActionsPerUtcDay)
            context.addIssue({
                code: "custom",
                path: ["maxPaidProviderActionsPerUserPerUtcDay"],
                message: "The per-user paid-provider quota cannot exceed the campaign quota.",
            });
    }
});
export const sharedTestGiftTemplateOptionSchema = z
    .object({
    templateId: sharedTestGiftTemplateIdSchema,
    name: z.string().min(2).max(160),
    workflowKind: z.enum(["INTERVIEW", "PICTURE_BOOK"]),
    availability: z.enum(["WORKING", "SANDBOX"]),
    readiness: z.enum(["AVAILABLE_WORKFLOW", "SANDBOX_WORKFLOW"]),
})
    .strict();
export const sharedTestGiftChallengeMetadataSchema = z
    .object({
    kind: z.literal("SHARED_TEST_GIFT"),
    campaignId: sharedTestGiftCampaignIdSchema,
    reusable: z.literal(true),
    selectionRequired: z.literal(true),
    paidProviderAccess: sharedTestGiftPaidProviderAccessSchema,
    allowedTemplates: z
        .array(sharedTestGiftTemplateOptionSchema)
        .length(bookTemplates.length)
        .refine((templates) => new Set(templates.map(({ templateId }) => templateId)).size === templates.length, "Allowed template options must be unique."),
})
    .strict();
export const sharedTestGiftRedemptionAuditSchema = z
    .object({
    id: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/iu),
    campaignId: sharedTestGiftCampaignIdSchema,
    userId: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/iu),
    bookId: z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/iu),
    templateId: sharedTestGiftTemplateIdSchema,
    paidProviderAccess: sharedTestGiftPaidProviderAccessSchema,
    createdAt: z.string().datetime(),
    reusable: z.literal(true),
    test: z.literal(true),
    simulation: z.literal(true),
    physicalShipment: z.literal(false),
    notice: z.literal(SIMULATED_PRINT_NOTICE),
})
    .strict();
//# sourceMappingURL=shared-test-gift.js.map